import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuListProps,
  MenuOptionGroup,
  MenuProps,
  Text,
  TextProps,
} from '@chakra-ui/react'
import React from 'react'

import { IconChevronDown } from '~shared-components/icons/brand/IconChevronDown'
import { IconChevronUp } from '~shared-components/icons/brand/IconChevronUp'

import { Helper } from '../Helper/Helper'

const customScrollbarStyles = {
  '&::-webkit-scrollbar': {
    width: 3.5,
  },
  '&::-webkit-scrollbar-thumb': {
    '&:hover': {
      boxShadow: 'inset 0 0 0 20px #d9d9d9',
    },
    border: '4px transparent solid',
    borderRadius: '30px',
    boxShadow: 'inset 0 0 0 20px #c8c8c8',
    transition: 'all 0.2s ease-in',
  },
}

const sxMenuButton = {
  '& p': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  _active: {
    bg: 'none',
    border: 0,
    outline: '3px solid',
    outlineColor: 'blue.50',
    outlineOffset: '-3px',
  },
  _disabled: {
    bg: 'neutral.10',
    color: 'neutral.20',
    cursor: 'not-allowed',
  },
  _focus: {
    border: 0,
    outline: '3px solid',
    outlineColor: 'blue.50',
    outlineOffset: '-3px',
  },
  _hover: {
    _disabled: {
      bg: 'neutral.10',
    },
    borderColor: 'deepPurple',
  },
  px: 2,
}

const sxMenuItemOption = {
  _hover: {
    bg: 'neutral.10',
    color: 'deepPurple',
  },
  bg: 'white.0',
}

const sxMenuItemOptionWithHighlight = {
  ...sxMenuItemOption,
  _checked: {
    _hover: { bg: 'blue.10' },
    bg: 'blue.10',
    color: 'deepPurple',
  },
}
export interface DropdownOption {
  default?: boolean
  description: string
  disabled?: boolean
  selected?: boolean
  value: string
}

export interface DropdownButtonDisplayProps<V extends DropdownOption>
  extends TextProps {
  disabled: V['disabled']
  options: V[]
  value: V['value'] | undefined
}

export interface DropdownOptionDisplayProps<V extends DropdownOption>
  extends TextProps {
  isSelected?: boolean
  option: V
}
export interface StandardDropdownProps<V extends DropdownOption>
  extends MenuListProps {
  afterAddon?: React.FC<DropdownButtonDisplayProps<V>>
  'aria-labelledby'?: string
  beforeAddon?: React.FC<DropdownButtonDisplayProps<V>>
  'data-testid'?: string
  disabled?: boolean
  displayButton?: React.FC<DropdownButtonDisplayProps<V>>
  displayOption?: React.FC<DropdownOptionDisplayProps<V>>
  error?: boolean
  helper?: string
  noSelectionHighlight?: boolean
  onOptionSelect?: (value: V['value'], option?: V) => void
  options: V[]
  optionsMaxHeight?: string
  optionsMinWidth?: string
  placement?: MenuProps['placement']
  value: V['value'] | undefined
}

export const DefaultButtonDisplay = <V extends DropdownOption>({
  disabled,
  options,
  value,
  ...props
}: React.PropsWithChildren<DropdownButtonDisplayProps<V>>) => {
  const displayValue =
    options?.find(({ value: v }) => v === value)?.description || value || ''
  return (
    <Text color={disabled ? 'neutral.20' : 'neutral.90'} {...props}>
      {displayValue}
    </Text>
  )
}

export const DefaultOptionDisplay = <V extends DropdownOption>({
  isSelected,
  option,
  ...props
}: React.PropsWithChildren<DropdownOptionDisplayProps<V>>) => (
  <Text
    as="span"
    className={isSelected ? 'display-option-selected' : ''}
    h="100%"
    w="100%"
    {...props}
  >
    {option.description || option.value}
  </Text>
)

export const StandardDropdown = <V extends DropdownOption = DropdownOption>({
  'aria-labelledby': ariaLabelledBy,
  'data-testid': dataTestId,
  css,
  children,
  sx,
  disabled = false,
  error,
  helper,
  noSelectionHighlight,
  onOptionSelect,
  options,
  optionsMaxHeight,
  optionsMinWidth,
  placement = 'bottom-start',
  value = '',
  maxWidth,
  maxHeight = '300px',
  displayOption: DisplayOption = DefaultOptionDisplay,
  displayButton: DisplayButton = DefaultButtonDisplay,
  afterAddon: AfterAddon,
  beforeAddon: BeforeAddon,
  ...listProps
}: React.PropsWithChildren<StandardDropdownProps<V>>) => {
  return (
    <Box>
      <Menu
        closeOnSelect
        flip
        matchWidth
        gutter={0}
        placement={placement}
        strategy="fixed"
      >
        {({ isOpen, onClose }) => (
          <>
            <MenuButton
              isFullWidth
              aria-labelledby={ariaLabelledBy}
              as={Button}
              borderColor={error ? 'red.70' : 'neutral.30'}
              className="dropdown-menu-button"
              css={css}
              data-testid={dataTestId}
              disabled={disabled}
              flexShrink={0}
              rightIcon={
                <Flex
                  alignItems="center"
                  bgColor="neutral.10"
                  borderRadius="2px"
                  height="24px"
                  justifyContent="center"
                  padding={1}
                  width="24px"
                >
                  {isOpen ? (
                    <IconChevronUp boxSize="16px" color="neutral.70" />
                  ) : (
                    <IconChevronDown boxSize="16px" color="neutral.70" />
                  )}
                </Flex>
              }
              sx={sx || sxMenuButton}
              textAlign="left"
              variant="outline"
            >
              <DisplayButton
                disabled={disabled}
                maxWidth={maxWidth}
                options={options}
                value={value}
              />
            </MenuButton>
            <MenuList
              maxH={maxHeight}
              maxWidth={maxWidth}
              overflowX="hidden"
              overflowY="auto"
              sx={customScrollbarStyles}
              {...listProps}
              className="dropdown-menu-list"
              px={4}
              zIndex="dropdown"
            >
              {BeforeAddon && (
                <BeforeAddon
                  disabled={disabled}
                  options={options}
                  value={value}
                />
              )}
              <MenuOptionGroup
                defaultValue={value}
                onChange={value =>
                  onOptionSelect &&
                  onOptionSelect(
                    value as V['value'],
                    options.find(o => o.value === value),
                  )
                }
                type="radio"
                value={value}
              >
                {options.map(option => {
                  const isSelected = value === option.value

                  return (
                    <MenuItemOption
                      key={option.value}
                      className={`dropdown-menu-button ${
                        isSelected ? 'selected' : ''
                      }`}
                      color="neutral.90"
                      fontSize="md"
                      icon={<></>}
                      iconSpacing={0}
                      isDisabled={option.disabled}
                      maxH={optionsMaxHeight}
                      minW={optionsMinWidth}
                      onClick={onClose}
                      sx={
                        noSelectionHighlight
                          ? sxMenuItemOption
                          : sxMenuItemOptionWithHighlight
                      }
                      value={option.value}
                    >
                      <DisplayOption isSelected={isSelected} option={option} />
                    </MenuItemOption>
                  )
                })}
                {children}
              </MenuOptionGroup>
              {AfterAddon && (
                <AfterAddon
                  disabled={disabled}
                  options={options}
                  value={value}
                />
              )}
            </MenuList>
          </>
        )}
      </Menu>
      {helper && <Helper error={Boolean(error)}>{helper}</Helper>}
    </Box>
  )
}
